import React from "react";
import VersionBadge from "./VersionBadge";
import "./Sidebar.css";

// SVG icons
const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 12L6 8L10 4" />
  </svg>
);
const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 12L10 8L6 4" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M8 3V13M3 8H13" />
  </svg>
);
const AppsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="5.5" height="5.5" rx="1" />
    <rect x="10.5" y="2" width="5.5" height="5.5" rx="1" />
    <rect x="2" y="10.5" width="5.5" height="5.5" rx="1" />
    <rect x="10.5" y="10.5" width="5.5" height="5.5" rx="1" />
  </svg>
);
const HistoryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="9" r="7" />
    <path d="M9 5V9L12 11" />
  </svg>
);
const DevicesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="14" height="10" rx="1" />
    <path d="M6 16H12M9 13V16" />
  </svg>
);
const WebhooksIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2L11 7H7L9 2Z" />
    <path d="M9 7V12" />
    <circle cx="9" cy="14" r="2" />
    <path d="M3 10C3 10 5 8 9 8C13 8 15 10 15 10" />
  </svg>
);

const PlayerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2L15 5.5V12.5L9 16L3 12.5V5.5L9 2Z" />
    <path d="M9 9L15 5.5M9 9V16M9 9L3 5.5" />
  </svg>
);

const LogsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4H15M3 7.5H12M3 11H14M3 14.5H10" />
  </svg>
);

const IntegrationsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2V7M9 11V16M2 9H7M11 9H16" />
    <circle cx="9" cy="9" r="2" />
  </svg>
);

// Admin-only icon for the AI Usage page (dollar / receipt feel).
const UsageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2H14V16L11.5 14L9 16L6.5 14L4 16V2Z" />
    <path d="M7 6H11M7 9H11M7 12H9" />
  </svg>
);

const navItems = [
  { id: "apps", label: "Apps", icon: <AppsIcon /> },
  { id: "integrations", label: "Integrations", icon: <IntegrationsIcon /> },
  // Conversation-history sidebar entry removed 2026-04-22 — it conflicted
  // semantically with the per-app ⟲ History button (which is for code
  // versioning / revert). The underlying HistoryPage + /api/chat/conversations
  // are intentionally preserved so the feature can be restored later; the
  // Apps list is now the entry point (with a "Resume previous conversation"
  // banner inside the app editor when a prior session exists).
  { id: "logs", label: "Logs", icon: <LogsIcon /> },
  { id: "devices", label: "Devices", icon: <DevicesIcon /> },
  { id: "webhooks", label: "Webhooks", icon: <WebhooksIcon /> },
  { id: "player", label: "Player", icon: <PlayerIcon /> },
  // Usage is monetization data — admins only. The render filter below
  // skips items with adminOnly:true when user.role !== "admin".
  { id: "usage", label: "Usage", icon: <UsageIcon />, adminOnly: true },
];

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="6" r="3.5" />
    <path d="M2.5 16.5C2.5 13.5 5.2 11 9 11C12.8 11 15.5 13.5 15.5 16.5" />
  </svg>
);

export default function Sidebar({ open, onToggle, activePage, onNavigate, onNewApp, onClose, user, onLogout }) {
  return (
    <>
      <aside className={`sidebar ${open ? "" : "collapsed"}${onClose ? " sidebar-mobile" : ""}`}>
        <button className="sidebar-toggle" onClick={onToggle} title={open ? "Collapse" : "Expand"}>
          {open ? <ChevronLeft /> : <ChevronRight />}
        </button>

        <button
          className="sidebar-new-btn"
          onClick={() => { onNewApp(); if (onClose) onClose(); }}
          title="Create new app"
        >
          <PlusIcon />
          <span className="sidebar-label">New App</span>
        </button>

        <nav className="sidebar-nav">
          {navItems
            // Hide adminOnly entries from non-admins. The page itself
            // is also auth-gated server-side, so a curl-savvy non-admin
            // won't get data either — this is just to keep the nav tidy.
            .filter((item) => !item.adminOnly || user?.role === "admin")
            .map((item) => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${activePage === item.id ? "active" : ""}`}
              onClick={() => { onNavigate(item.id); if (onClose) onClose(); }}
              title={item.label}
            >
              {item.icon}
              <span className="sidebar-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Spacer pushes user to bottom */}
        <div className="sidebar-spacer" />

        {/* User profile area */}
        {user && (
          <div className="sidebar-user" title={open ? undefined : user.name || user.email}>
            <div className="sidebar-user-avatar">
              <UserIcon />
            </div>
            {open && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{user.name || user.email}</span>
                <div className="sidebar-user-actions">
                  <button className="sidebar-user-logout" onClick={onLogout}>Sign out</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Deploy version — always visible. Matches the SHA posted in
            release notes so ops can correlate "what's live" at a glance. */}
        <VersionBadge collapsed={!open} />
      </aside>

      {onClose && <div className="sidebar-backdrop" onClick={onClose} />}
    </>
  );
}
